import httpClient, { ApiResponseType, FORM_DATA_CONFIG, queryBuilder } from 'packages/http_client';
import { convertApiError } from 'packages/http_client/utils/converters';
import identity from 'utils/identity';

import {
  ApiStatusType,
  CountApiResponseType,
  CreateVentureBuilderApiResponseType,
  CreateVentureBuilderPayload,
  CreateVentureBuilderResult,
  EditVentureBuilderPayload,
  ItemApiResponseType,
  ListApiResponseType,
  ManagedStartupApiType,
  ManagedStartupInput,
  ManagedStartupType,
  StartupsCountApiType,
  VentureBuilderApiType,
  VentureBuildersPage,
  VentureBuildersQuery,
  VentureBuilderType,
} from '../types';
import {
  CREATE_VENTURE_BUILDER_API,
  MANAGED_STARTUP_API,
  VENTURE_BUILDER_API,
  convertCreateVentureBuilderRequest,
  convertEditVentureBuilderRequest,
  convertManagedStartupResponse,
  convertVentureBuilderResponse,
} from '../utils';

const DEFAULT_ERROR = 'Something went wrong. Please try again';

// uAdmin may return HTTP 200 with status: "error"; React Query needs a rejected promise.
export const unwrap = <T extends ApiStatusType>(response: ApiResponseType<T>): T => {
  const { data, error } = response;
  if (error) {
    throw new Error(error.message || DEFAULT_ERROR);
  }
  if (!data) {
    throw new Error(DEFAULT_ERROR);
  }
  if (data.status === 'error') {
    throw new Error(convertApiError(data).message || DEFAULT_ERROR);
  }
  return data;
};

const getStartupsCounts = async (ventureBuilderIds: number[]): Promise<Record<number, number>> => {
  if (identity.isEmptyArray(ventureBuilderIds)) {
    return {};
  }
  const params = queryBuilder({ $f: 'venture_builder_id,id__count', $groupby: 'venture_builder_id' })
    .in({ venture_builder_id: ventureBuilderIds })
    .toObject();
  const response = await httpClient.get<ApiResponseType<ListApiResponseType<StartupsCountApiType>>>(
    MANAGED_STARTUP_API,
    { params },
  );
  const { result } = unwrap(response);

  return (result || []).reduce<Record<number, number>>((counts, row) => {
    counts[row.venture_builder_id] = row.id__count;
    return counts;
  }, {});
};

export const getVentureBuilders = async (query: VentureBuildersQuery): Promise<VentureBuildersPage> => {
  const search = query.searchString?.trim();
  const listParams = queryBuilder().limit(query.limit).offset(query.offset).orderByDescending('id');
  const countParams = queryBuilder({ $f: 'id__count' });
  if (identity.isTruthyString(search)) {
    listParams.searchQuery(search!);
    countParams.searchQuery(search!);
  }

  const [listResponse, countResponse] = await Promise.all([
    httpClient.get<ApiResponseType<ListApiResponseType<VentureBuilderApiType>>>(
      VENTURE_BUILDER_API,
      { params: listParams.toObject() },
    ),
    httpClient.get<ApiResponseType<CountApiResponseType>>(
      VENTURE_BUILDER_API,
      { params: countParams.toObject() },
    ),
  ]);

  const ventureBuilders = (unwrap(listResponse).result || []).map(convertVentureBuilderResponse);
  const total = unwrap(countResponse).result?.[0]?.id__count || 0;
  const counts = await getStartupsCounts(ventureBuilders.map(({ id }) => id));

  return {
    total,
    items: ventureBuilders.map((vb) => ({ ...vb, startupsCount: counts[vb.id] || 0 })),
  };
};

export const getVentureBuilderById = async (id: number): Promise<VentureBuilderType> => {
  const params = queryBuilder().preload().toObject();
  const response = await httpClient.get<ApiResponseType<ItemApiResponseType<VentureBuilderApiType>>>(
    `${VENTURE_BUILDER_API}/${id}`,
    { params },
  );
  if (response.httpStatus === 404) {
    throw new Error('Venture Builder not found');
  }
  const { result } = unwrap(response);
  if (!identity.isTruthyNumber(result?.id)) {
    throw new Error('Venture Builder not found');
  }

  return convertVentureBuilderResponse(result!);
};

export const getManagedStartups = async (ventureBuilderId: number): Promise<ManagedStartupType[]> => {
  const params = queryBuilder({ venture_builder_id: ventureBuilderId }).preload().orderBy('id').toObject();
  const response = await httpClient.get<ApiResponseType<ListApiResponseType<ManagedStartupApiType>>>(
    MANAGED_STARTUP_API,
    { params },
  );

  return (unwrap(response).result || []).map(convertManagedStartupResponse);
};

export const createVentureBuilder = async (
  payload: CreateVentureBuilderPayload,
): Promise<CreateVentureBuilderResult> => {
  const response = await httpClient.post<ApiResponseType<CreateVentureBuilderApiResponseType>>(
    CREATE_VENTURE_BUILDER_API,
    convertCreateVentureBuilderRequest(payload),
    FORM_DATA_CONFIG,
  );
  const data = unwrap(response);

  return {
    ventureBuilderId: data.venture_builder_id || 0,
    startupIds: data.managed_startup_ids || [],
  };
};

export const updateVentureBuilder = async (id: number, payload: EditVentureBuilderPayload) => {
  const response = await httpClient.put<ApiResponseType<ApiStatusType>>(
    `${VENTURE_BUILDER_API}/${id}`,
    convertEditVentureBuilderRequest(payload),
    FORM_DATA_CONFIG,
  );
  unwrap(response);
};

export const createManagedStartup = async (ventureBuilderId: number, payload: ManagedStartupInput) => {
  const response = await httpClient.post<ApiResponseType<ApiStatusType>>(
    MANAGED_STARTUP_API,
    { _venture_builder_id: ventureBuilderId, _name: payload.name.trim(), _email: payload.email.trim() },
    FORM_DATA_CONFIG,
  );
  unwrap(response);
};

export const updateManagedStartup = async (id: number, name: string) => {
  const response = await httpClient.put<ApiResponseType<ApiStatusType>>(
    `${MANAGED_STARTUP_API}/${id}`,
    { _name: name.trim() },
    FORM_DATA_CONFIG,
  );
  unwrap(response);
};
